import { createAuth0Client } from '@auth0/auth0-spa-js';
import { DOCTYPE_ACCESS_LOG } from '@krispsystems/constants';
import { removeLocalStorage } from 'ks-client-admin/utils/local-storage';

import { getCookie, getLocalStorage, removeCookie } from '../../utils';
import { createLogMessage } from '../../sagas/logging/sagas_logging';

const KRISP_DOMAIN = process.env.REACT_APP_KRISP_DOMAIN;
const KRISP_API_HOST = process.env.REACT_APP_KRISP_API_HOST;
const KRISP_ENV_ID = process.env.REACT_APP_KRISP_ENV;
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID;
const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN;

let auth0Client;

export function isAppTokenExisting() {
  const appToken = getCookie('ks-app-token-admin');
  return Boolean(appToken);
}

async function initAuth0() {
  if (!auth0Client) {
    auth0Client = await createAuth0Client({
      domain: AUTH0_DOMAIN,
      clientId: AUTH0_CLIENT_ID,
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
        redirect_uri: `${window.location.origin}/admin`,
      },
    });
  }

  return auth0Client;
}

export async function authenticate() {
  const oldKrispJwtToken = getCookie('ks-app-token-admin');

  if (oldKrispJwtToken) {
    const oldJwtStrBuff = oldKrispJwtToken.split('.');
    const oldJwtPayloadDecode = atob(oldJwtStrBuff[1]);
    const oldKrispJwtPayload = JSON.parse(oldJwtPayloadDecode);

    const { exp } = oldKrispJwtPayload;
    const isExpired = Date.now() >= exp * 1000;

    if (isExpired) return { isExpired };

    return {
      payload: oldKrispJwtPayload,
      token: oldKrispJwtToken,
    };
  };

  const auth0 = await initAuth0();
  const { location, history } = window;
  const { search } = location;

  if (search.includes("code=") && search.includes("state=")) {
    const { appState } = await auth0.handleRedirectCallback();
    const { return_to } = appState || {};
    history.replaceState({}, document.title, return_to || '/admin');
  }

  try {
    const auth0Token = await auth0.getTokenSilently({
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
      },
    });

    const newKrispJwtTokenRes = await fetch(`https://${KRISP_API_HOST}/auth0/exchange-token`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth0Token}`,
      },
    });

    const newKrispJwtToken = await newKrispJwtTokenRes.json();
    return newKrispJwtToken;
  } catch (err) {
    switch (err.error) {
      case 'login_required':
        await auth0Relogin(window.location.path);
        break;

      default:
        return;
    }
  }
}

export async function auth0Relogin(return_to) {
  const auth0 = await initAuth0();
  const primarySubsidiaryLogo = getLocalStorage('krisp_primary_subsidiary_logo');
  const companyPrimarySubsidiaryLogo = getLocalStorage('krisp_company_primary_subsidiary_logo');
  const logo = primarySubsidiaryLogo || companyPrimarySubsidiaryLogo || '';

  auth0.loginWithRedirect({
    appState: {
      return_to,
      api_host: KRISP_API_HOST,
      domain: KRISP_DOMAIN,
      env_id: KRISP_ENV_ID,
    },
    authorizationParams: {
      company_logo: logo,
    },
  });
}

export async function logout(options) {
  const { redirect = true, user } = options || {};

  if (user) {
    const token = getCookie('ks-app-token-admin');
    const log = createLogMessage({ user, action: 'logout' });

    try {
      await fetch(`https://${KRISP_API_HOST}/${DOCTYPE_ACCESS_LOG}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(log),
      });
    } catch (err) {
      console.log('Unable to create a logout access log', user);
    }
  }

  removeCookie('ks-app-token-admin');
  removeCookie('ks-app-token-admin', {
    path: '/',
    domain: `.krispsystems.net`,
    secure: true,
  });

  removeLocalStorage('admin-user');

  if (!redirect) return;

  await auth0Relogin();
}
